import type { HTMLAttributes, ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatBadgeCount } from '@/lib/notificationStyles'
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export type HomeNavItem = {
  id: string
  label: string
  to: string
  tile: string
  Icon: LucideIcon
  /** Feature off for the tenant: greyed and inert. */
  disabled: boolean
}

/**
 * Shown on a greyed card, as a tooltip and inside its aria-label. One constant
 * because the e2e page object locates disabled cards by that label.
 */
const DISABLED_REASON = 'Not enabled for this tenant'

export const CARD_SHELL =
  'group flex w-full flex-col items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-2 py-2.5 shadow-sm'

/**
 * Icon, badge and label. Shared by the live, greyed and editing cards so they
 * cannot drift in size or spacing — a greyed tile that reflowed the grid would
 * read as a layout bug rather than as a disabled module.
 */
export function CardBody({
  item,
  badge,
  disabled,
  underline = true,
}: {
  item: HomeNavItem
  badge: number
  disabled: boolean
  underline?: boolean
}) {
  const Icon = item.Icon
  return (
    <>
      <div
        className={cn(
          'relative flex h-14 w-14 items-center justify-center rounded-lg',
          disabled ? 'bg-gray-200' : item.tile,
        )}
      >
        <Icon className={cn('h-7 w-7', disabled ? 'text-gray-400' : 'text-white')} />
        {/* No badge while greyed: an unread count you cannot open is noise. */}
        {!disabled && badge > 0 && (
          <span className='absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white'>
            {formatBadgeCount(badge)}
          </span>
        )}
      </div>
      <span
        className={cn(
          'flex h-[26px] max-w-[84px] items-start justify-center text-center text-[10px] font-semibold leading-tight',
          // The underline is the affordance that says "link", so a card that
          // cannot be clicked must not carry it.
          disabled ? 'text-gray-400' : cn('text-gray-700', underline && 'underline'),
        )}
      >
        {item.label}
      </span>
    </>
  )
}

/**
 * One nav tile. A module the tenant does not have renders GREYED AND INERT
 * rather than disappearing.
 *
 * Disappearing was the wrong signal: a user who opened Inventory yesterday and
 * cannot find it today has no way to tell "an admin switched the module off"
 * from "the app is broken", and no word left on screen to search for or quote
 * in a support ticket. The greyed card keeps the module named and says why.
 *
 * Inert, not merely a greyed link, because a click would land on /no-access —
 * a page that says the user's ACCOUNT is disabled and offers a sign-out button.
 * Account-level language for what is really tenant configuration would read as
 * being locked out of the product.
 *
 * Only the FEATURE gate greys. A card the user's role forbids stays hidden (see
 * HomeNavPanel): a module being off is a fact about the tenant anyone may
 * know, while a screen someone's permissions forbid is not worth advertising
 * back at them.
 *
 * `press` carries the long-press handlers that open edit mode.
 */
export function HomeTileCard({
  item,
  badge,
  press,
  corner,
}: {
  item: HomeNavItem
  badge: number
  press?: HTMLAttributes<HTMLElement>
  /**
   * Its own control pinned to the icon's top-left (the orange LIS queue
   * count). A sibling of the link, not a child: a button inside an anchor is
   * invalid, and its click would also follow the link.
   */
  corner?: ReactNode
}) {
  if (item.disabled) {
    return (
      <UiTooltip>
        <TooltipTrigger asChild>
          {/* A div, not a disabled link: no href, and no tabIndex either, so
              keyboard users skip it instead of landing on something inert. The
              reason rides on the aria-label as well as the tooltip, because
              Radix tooltips never fire on touch. */}
          <div
            {...press}
            aria-disabled='true'
            aria-label={`${item.label}. ${DISABLED_REASON}`}
            className={cn(CARD_SHELL, 'cursor-not-allowed select-none')}
          >
            <CardBody item={item} badge={badge} disabled />
          </div>
        </TooltipTrigger>
        <TooltipContent>{DISABLED_REASON}</TooltipContent>
      </UiTooltip>
    )
  }

  const link = (
    <NavLink
      {...press}
      to={item.to}
      draggable={false}
      aria-label={badge > 0 ? `${item.label}, ${badge} unread` : item.label}
      className={cn(CARD_SHELL, 'select-none transition-opacity [-webkit-touch-callout:none] hover:opacity-80')}
    >
      <CardBody item={item} badge={badge} disabled={false} />
    </NavLink>
  )
  if (!corner) return link
  return (
    <div className='relative w-full'>
      {link}
      {/* The icon is 56px wide and centred, so its left edge is 28px left of centre. */}
      <div className='absolute left-[calc(50%-34px)] top-1.5'>{corner}</div>
    </div>
  )
}
